import { encodeSqrtRatioX96 as sdkEncodeSqrtRatioX96 } from "@uniswap/v3-sdk"

import type { TokenStore } from "@/domains/tokens/token-store"

import { toBigInt, toJSBI } from "./jsbi-bigint"

/**
 * Encode a human-readable price into a sqrtPriceX96.
 *
 * `price` is the amount of token1 per 1 unit of token0 (both in human-readable form).
 * If `invert` is true, the caller instead typed "token0 per 1 unit of token1".
 *
 * token0/token1 must be the canonical (address-sorted) pool tokens; the caller is
 * responsible for ordering them before calling.
 */
export function priceToSqrtRatioX96(args: {
  price: string
  token0: TokenStore
  token1: TokenStore
  invert?: boolean
}): bigint {
  const { price, token0, token1, invert } = args

  if (!price || price === ".") {
    throw new Error("priceToSqrtRatioX96: empty price")
  }
  const parsed = parseFloat(price)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`priceToSqrtRatioX96: invalid price "${price}"`)
  }

  // Uniswap's encodeSqrtRatioX96(amount1, amount0) expects raw units.
  // price = amount1 / amount0 in human units; so baseToken=token0 (1 unit),
  // quoteToken=token1 (price units), then raw = parseUnits.
  const baseAmount = invert ? price : "1"
  const quoteAmount = invert ? "1" : price

  const baseRaw = token0.parseUnits(baseAmount)
  const quoteRaw = token1.parseUnits(quoteAmount)

  return toBigInt(sdkEncodeSqrtRatioX96(toJSBI(quoteRaw), toJSBI(baseRaw)))
}

/**
 * Inverse of priceToSqrtRatioX96. Returns a human-readable price string
 * representing token1 per 1 token0 (or the inverse if invert=true).
 */
export function sqrtRatioX96ToPrice(args: {
  sqrtPriceX96: bigint
  token0: TokenStore
  token1: TokenStore
  invert?: boolean
  decimalPlaces?: number
}): string {
  const { sqrtPriceX96, token0, token1, invert, decimalPlaces } = args
  if (sqrtPriceX96 <= 0n) return "0"

  // price (token1/token0, raw) = sqrtPriceX96^2 / 2^192
  // Scale by 10^(token0.decimals - token1.decimals) to convert to human units.
  const Q192 = 1n << 192n
  const num = sqrtPriceX96 * sqrtPriceX96
  // Use precision buffer to avoid integer truncation.
  const precision = 10n ** 36n
  const scaled = (num * precision) / Q192

  const decimalScale = 10n ** BigInt(Math.max(0, token0.decimals - token1.decimals))
  const decimalUnscale = 10n ** BigInt(Math.max(0, token1.decimals - token0.decimals))

  // token1/token0 (human) = scaled * 10^(dec0 - dec1) / precision
  const humanNum = scaled * decimalScale
  const humanDen = precision * decimalUnscale

  const asNumber = Number(humanNum) / Number(humanDen)
  const price = invert ? 1 / asNumber : asNumber
  if (!Number.isFinite(price)) return "0"
  const places = decimalPlaces ?? 18
  return price.toFixed(places).replace(/\.?0+$/, "")
}

/**
 * Encode an explicit (baseAmount, quoteAmount) pair using their respective TokenStore
 * decimals. Result is sqrt(quoteRaw / baseRaw) * 2^96. Uniswap's canonical helper.
 */
export function encodeSqrtRatioX96(args: {
  baseToken: TokenStore
  quoteToken: TokenStore
  baseAmount: string
  quoteAmount: string
}): bigint {
  const baseRaw = args.baseToken.parseUnits(args.baseAmount)
  const quoteRaw = args.quoteToken.parseUnits(args.quoteAmount)
  return toBigInt(sdkEncodeSqrtRatioX96(toJSBI(quoteRaw), toJSBI(baseRaw)))
}
